// Transport der Oberfläche: fetch gegen /api mit Sitzungs-Cookie und CSRF-Header, Umschlag → Ergebnis oder
// AppError. Typisiert über die Verträge – wer eine Aktion aufruft, übergibt das Vertragsobjekt, nie einen Namen.
// Beschreibung der Gegenseite: docs/API.md.
import {
  AUTH_ROUTES,
  type ChangePasswordBody,
  type ChangePasswordResult,
  type ForgetDeviceBody,
  type InvitationAcceptBody,
  type InvitationAcceptResult,
  type InvitationInfo,
  type LoginMfaBody,
  type MfaConfirmResult,
  type MfaSetupResult,
  type PasswordCheckResult,
  type ReauthBody,
  type ResetCheckResult,
  type ResetPasswordBody,
  type RevokeSessionBody,
  type SecurityEvent,
  type SessionsResult,
} from '@shared/auth'
import type { AnyActionContract, InputArg, InputOf, OutputOf } from '@shared/contract'
import { actionContracts } from '@shared/contracts.gen'
import { brand } from '@shared/brand'
import { AppError, fromErrorPayload, isAppError, type ErrorCode } from '@shared/errors'
import { MAIL_PUBLIC_ROUTES, type UnsubscribeInfo } from '@shared/mail'
import { reviveDates } from './dates'
import type {
  ActionMeta,
  ActionOutcome,
  ActionPlan,
  AuthConfig,
  LoginInput,
  SessionPayload,
  UploadedFile,
  UploadOptions,
} from './types'

/** Pflicht-Header jeder ändernden Anfrage (docs/API.md §2). */
export const CSRF_HEADER = 'X-Merxo-Csrf'

/**
 * Kennzeichen der Merxo-Kante (vorgeschalteter Proxy, `deploy/hetzner/Caddyfile.example`): steht auf allen Antworten
 * der Kante, auch auf ihren eigenen Fehlerantworten, wenn die Anwendung nicht antwortet (docs/kasse-offline/
 * SPEZIFIKATION.md §10.1). Ohne Kante (z. B. Render) fehlt es – dann gibt es kein `edge`.
 */
export const EDGE_HEADER = 'X-Merxo-Edge'

/**
 * Einordnung einer Antwort durch den Transport (docs/kasse-offline/SPEZIFIKATION.md §10.1). Dieselben Werte wie
 * `REACH_REASONS` der Kasse (modules/pos/lib/offline/types.ts) – die Hülle führt sie selbst, sie kennt kein Modul.
 *   network  fetch wirft (kein Netz, Verbindung abgebrochen, DNS)                          → keine Antwort
 *   timeout  Zeitlimit des Aufrufs abgelaufen (auch über das Signal des Aufrufers)          → keine Antwort
 *   foreign  Antwort ohne Merxo-Umschlag und ohne Kennzeichen der Kante, Umleitung nach fremd → keine Antwort
 *   edge     ohne Umschlag, aber mit X-Merxo-Edge und 5xx: Kante erreichbar, Anwendung nicht → Merxo antwortet (Störung)
 *   server   Merxo-Umschlag mit 5xx, LOCK_TIMEOUT oder 429                                  → Merxo antwortet (Störung)
 *   ok       Merxo-Umschlag mit Ergebnis oder fachlichem Fehler                              → Merxo antwortet
 */
export const REACH_REASONS = ['network', 'timeout', 'foreign', 'edge', 'server', 'ok'] as const
export type ReachReason = (typeof REACH_REASONS)[number]
/** Nur diese Einordnungen zählen als „keine Antwort“ – nur sie dürfen eine Kasse in den Betrieb ohne Verbindung führen. */
export const NO_ANSWER_REASONS: readonly ReachReason[] = ['network', 'timeout', 'foreign']

/** Zeitlimit der Gesundheitsprüfung (docs/kasse-offline/SPEZIFIKATION.md §10.1, `HEALTH_TIMEOUT_MS` der Kasse) */
export const HEALTH_TIMEOUT_MS = 3_000

const NETWORK_MESSAGE = 'Keine Verbindung zum Server. Bitte prüfen Sie Ihre Verbindung und versuchen Sie es erneut.'
const TIMEOUT_MESSAGE =
  'Der Server hat nicht rechtzeitig geantwortet. Bitte prüfen Sie Ihre Verbindung und versuchen Sie es erneut.'
const FOREIGN_MESSAGE =
  'Keine Verbindung zum Server. Geantwortet hat eine andere Stelle, zum Beispiel die Anmeldeseite eines WLANs. Bitte prüfen Sie Ihre Verbindung.'
const UNREACHABLE_MESSAGE = 'Der Server ist gerade nicht erreichbar. Bitte versuchen Sie es gleich noch einmal.'

const TRANSPORT_REASONS: readonly unknown[] = ['network', 'timeout', 'foreign', 'edge']

/** Einordnung der Fehler dieses Clients, die keinen eigenen Grund tragen (Umschlag des Servers: `server` bzw. `ok`) */
const reachOfError = new WeakMap<AppError, ReachReason>()

/**
 * Einordnung eines Fehlers nach §10.1; `null`, wenn es kein Fehler des Transports ist (z. B. Abbruch durch den Aufrufer).
 * Fehler aus Attrappen (Tests) ordnen sich nach `details.reason` bzw. dem Code ein.
 */
export function reachReasonOf(error: unknown): ReachReason | null {
  if (!isAppError(error)) return null
  const known = reachOfError.get(error)
  if (known) return known
  const reason = error.details?.reason
  if (TRANSPORT_REASONS.includes(reason)) return reason as ReachReason
  // frühere Bezeichnung (eigener Transport des Assistenten): Antwort ohne Umschlag mit 5xx
  if (reason === 'unreachable') return 'foreign'
  return error.code === 'INTERNAL' || error.code === 'LOCK_TIMEOUT' ? 'server' : 'ok'
}

/** true: keine Antwort von Merxo (`network`, `timeout`, `foreign`) – ob die Anfrage angekommen ist, steht nicht fest. */
export const isNetworkError = (error: unknown): boolean => {
  const reason = reachReasonOf(error)
  return reason !== null && NO_ANSWER_REASONS.includes(reason)
}

/**
 * true: der Server ist für die Anzeige nicht erreichbar – keine Antwort oder nur die Kante antwortet (`edge`). Das
 * Ergebnis einer Anfrage ist auch hier unbekannt (502/504 der Kante); Wiederholen und „Erneut versuchen“ lohnen sich.
 */
export const isUnreachableError = (error: unknown): boolean => isNetworkError(error) || reachReasonOf(error) === 'edge'

const STATUS_CODES: Record<number, ErrorCode> = {
  400: 'VALIDATION',
  401: 'UNAUTHENTICATED',
  403: 'FORBIDDEN',
  404: 'NOT_FOUND',
  409: 'CONFLICT',
  413: 'VALIDATION',
  415: 'VALIDATION',
  422: 'BUSINESS_RULE',
  423: 'LOCK_TIMEOUT',
  428: 'CONFIRMATION_REQUIRED',
  429: 'LIMIT_EXCEEDED',
}

/** Kennung der Anfrage aus der Antwort (steht im Serverprotokoll und im Audit) – für „technische Kennung“. */
export const requestIdOf = (error: unknown): string | undefined => {
  const id = isAppError(error) ? error.details?.requestId : undefined
  return typeof id === 'string' ? id : undefined
}

export interface RunOptions {
  /** ein Schlüssel je Absenden; bei Wiederholung derselbe (docs/API.md §4) */
  idempotencyKey?: string
  /** effectHash der bestätigten Vorschau */
  expectedEffectHash?: string
  /**
   * Freigabe durch eine zweite Person an Ort und Stelle (Verträge mit `secondPerson: true`, docs/API.md §4): Sie
   * weist sich mit ihrer PIN aus, die Aktion läuft mit ihren Rechten – die angemeldete Person bleibt angemeldet.
   * Die PIN steht nur in dieser einen Anfrage; nie speichern, nie protokollieren.
   */
  approval?: SecondPersonApproval
  /**
   * Abbruch durch den Aufrufer. Ein Signal mit Zeitgrund (`AbortSignal.timeout(…)`) wird ein AppError mit
   * `details.reason = 'timeout'`; jeder andere Abbruch kommt unverändert zurück (kein Verbindungsproblem).
   */
  signal?: AbortSignal
  /** Zeitlimit dieses Aufrufs in ms (Antwort samt Rumpf); danach AppError mit `details.reason = 'timeout'` */
  timeoutMs?: number
}

export interface SecondPersonApproval {
  /** die freigebende Person – aus der Liste der Aufsichten (userId) oder per E-Mail-Adresse */
  userId?: string
  email?: string
  pin: string
}

export interface RequestOptions {
  signal?: AbortSignal
  /** Zeitlimit in ms (wie RunOptions.timeoutMs) */
  timeoutMs?: number
  /** 401 nicht als „Sitzung abgelaufen“ melden (Anmeldung, erste Sitzungsabfrage) */
  silentUnauthenticated?: boolean
}

/** Ergebnis der Gesundheitsprüfung GET /api/health (docs/kasse-offline/SPEZIFIKATION.md §10.1) */
export interface HealthCheck {
  /**
   * ok: 200, JSON, Produkt = Marke, Zeit lesbar · edge: Merxo antwortet, ist aber nicht bereit (503 mit Produkt der
   * Marke, Kante mit X-Merxo-Edge) · foreign/network/timeout: keine Antwort von Merxo
   */
  reason: Exclude<ReachReason, 'server'>
  /** HTTP-Status; null ohne Antwort */
  status: number | null
  /** Zeit des Servers aus der Antwort (nur bei lesbarer Antwort von Merxo) */
  serverTime: Date | null
  /** Dauer vom Absenden bis zur gelesenen Antwort in ms (Schätzung der Uhrabweichung) */
  elapsedMs: number
}

export interface HealthOptions {
  signal?: AbortSignal
  /** Standard HEALTH_TIMEOUT_MS (3 s) */
  timeoutMs?: number
}

export interface ApiClientOptions {
  /** Standard '/api' */
  baseUrl?: string
  fetch?: typeof fetch
  /** Sitzung abgelaufen oder beendet – die Hülle zeigt die Anmeldung */
  onUnauthenticated?: (error: AppError) => void
  /**
   * false nach einer Anfrage ohne Antwort von Merxo (`network`, `timeout`, `foreign`) oder nur mit Antwort der Kante
   * (`edge`); true nach der nächsten Antwort von Merxo
   */
  onReachability?: (reachable: boolean) => void
}

export interface ApiClient {
  /** Lesende oder ändernde Aktion ausführen; liefert die Ausgabe. Fehler sind immer AppError. */
  run<C extends AnyActionContract>(contract: C, input: InputArg<C>, options?: RunOptions): Promise<OutputOf<C>>
  /** wie run(), zusätzlich `meta` (angelegte Datensätze, Rückgängig-Angebot, Wiederholung) */
  runDetailed<C extends AnyActionContract>(
    contract: C,
    input: InputArg<C>,
    options?: RunOptions,
  ): Promise<ActionOutcome<OutputOf<C>>>
  /** Aktion nur über ihren Namen (Rückgängig-Angebote des Servers) */
  runByName(name: string, input: unknown, options?: RunOptions): Promise<ActionOutcome<unknown>>
  /** Probelauf einer ändernden Aktion: Vorschau und effectHash */
  plan<C extends AnyActionContract>(
    contract: C,
    input: InputArg<C>,
    options?: Pick<RunOptions, 'signal' | 'timeoutMs'>,
  ): Promise<ActionPlan<InputOf<C>>>
  authConfig(options?: RequestOptions): Promise<AuthConfig>
  login(input: LoginInput): Promise<SessionPayload>
  logout(input?: { everywhere?: boolean }): Promise<void>
  /** `silentUnauthenticated`: beim Start ist 401 der Normalfall „nicht angemeldet“ */
  session(options?: RequestOptions): Promise<SessionPayload>
  /**
   * Gesundheitsprüfung (GET /api/health, `cache: 'no-store'`, ohne Anmeldung): wirft nie, sondern ordnet ein (§10.1).
   * Nur ein Abbruch durch den Aufrufer (ohne Zeitgrund) kommt als Fehler zurück.
   */
  health(options?: HealthOptions): Promise<HealthCheck>
  selectTenant(tenantId: string): Promise<SessionPayload>
  setLocation(locationId: string | null): Promise<SessionPayload>
  /**
   * Datei ablegen (POST /api/files, multipart). An einen Datensatz hängt sie erst die Aktion des Moduls
   * (`fileId` in der Eingabe). Fehler sind AppError mit dem Satz des Servers.
   */
  upload(file: Blob, options?: UploadOptions): Promise<UploadedFile>

  // ---- Anmeldung und Zugang (docs/anmeldung/SPEZIFIKATION.md §8.1, Formen aus @shared/auth) ----
  // Keine Aktionen: Diese Routen laufen am Dispatcher vorbei (Kern, Eigentümer-Verbindung). Zeitpunkte bleiben
  // ISO-Texte. Kennwörter, Codes und Token stehen nur in der einen Anfrage – nie speichern, nie protokollieren.
  /** zweiter Schritt nach `401 mfa_required`: Code aus der App oder Wiederherstellungscode (Challenge-Cookie) */
  loginMfa(input: LoginMfaBody): Promise<SessionPayload>
  /**
   * Einrichtung beginnen (Sitzung mit frischer Bestätigung oder Challenge `mfa_setup`); Geheimnis genau einmal. Ist die
   * Zwei-Faktor-Anmeldung schon eingerichtet (auf ein neues Telefon übertragen): `code` des bisherigen Geräts oder ein
   * Wiederherstellungscode.
   */
  mfaSetup(input?: { code?: string }): Promise<MfaSetupResult>
  /** Einrichtung mit dem ersten Code bestätigen; beim Pflicht-Ablauf entsteht dabei die Sitzung */
  mfaConfirm(input: { code: string }): Promise<MfaConfirmResult<SessionPayload>>
  mfaDisable(input: { code: string }): Promise<{ ok: true }>
  /** neue Wiederherstellungscodes – entwertet alle alten */
  recoveryCodes(input: { code: string }): Promise<{ recoveryCodes: string[] }>
  /** frische Bestätigung dieser Sitzung (Kennwort ODER Code) – es entsteht keine neue Sitzung */
  reauth(input: ReauthBody): Promise<{ reauthAt: string }>
  changePassword(input: ChangePasswordBody): Promise<ChangePasswordResult>
  /** Hinweis beim Tippen (§6.1) – maßgeblich bleibt die Prüfung beim Speichern */
  passwordCheck(input: { password: string; email?: string }, options?: Pick<RequestOptions, 'signal'>): Promise<PasswordCheckResult>
  /** Antwort immer gleich – ob es das Konto gibt, verrät sie nicht */
  forgotPassword(input: { email: string }): Promise<{ ok: true }>
  /** gültiger Rücksetz-Link: maskierte Adresse, Code nötig?, Ablauf; sonst NOT_FOUND */
  resetCheck(input: { token: string }): Promise<ResetCheckResult>
  resetPassword(input: ResetPasswordBody): Promise<{ ok: true }>
  sessions(): Promise<SessionsResult>
  revokeSession(input: RevokeSessionBody): Promise<{ revoked: number }>
  forgetDevice(input: ForgetDeviceBody): Promise<{ ok: true }>
  securityEvents(input?: { limit?: number }): Promise<{ events: SecurityEvent[] }>
  invitationInfo(input: { token: string }): Promise<InvitationInfo>
  acceptInvitation(input: InvitationAcceptBody): Promise<InvitationAcceptResult>
  /** Abmelde-Link aus einer Benachrichtigung: Kategorie und Betrieb (§4.8) */
  unsubscribeInfo(input: { token: string }): Promise<UnsubscribeInfo>
  unsubscribe(input: { token: string }): Promise<{ ok: true }>
}

type AuthRouteName = keyof typeof AUTH_ROUTES

/**
 * Routen mit Sitzung, an denen ein 401 die Antwort auf die EINGABE ist (Kennwort oder Code falsch) und nicht „Sitzung
 * abgelaufen“ – sie melden 401 nicht an die Hülle. Routen ohne Sitzung (Anmeldung, Zurücksetzen, Einladung) ohnehin nicht.
 */
const CREDENTIAL_ROUTES: ReadonlySet<AuthRouteName> = new Set(['reauth', 'changePassword', 'mfaDisable', 'recoveryCodes'])

interface Envelope {
  result?: unknown
  meta?: unknown
  error?: unknown
}

/** Abbruchgrund „Zeit abgelaufen“ – `AbortSignal.timeout()` des Aufrufers bzw. das eigene Zeitlimit */
const isTimeoutReason = (reason: unknown): boolean =>
  typeof reason === 'object' && reason !== null && (reason as { name?: unknown }).name === 'TimeoutError'

/**
 * Führt `task` mit dem Signal des Aufrufers und – wenn gesetzt – einem eigenen Zeitlimit aus. Das Zeitlimit beendet den
 * Aufruf auch dann, wenn `fetch` das Signal nicht beachtet; es läuft über setTimeout (mit der Testuhr prüfbar).
 */
function withDeadline<T>(
  task: (signal: AbortSignal | undefined) => Promise<T>,
  signal: AbortSignal | undefined,
  timeoutMs: number | undefined,
): Promise<T> {
  if (!timeoutMs || timeoutMs <= 0) return task(signal)
  const controller = new AbortController()
  const forward = (): void => controller.abort(signal?.reason)
  if (signal?.aborted) forward()
  else signal?.addEventListener('abort', forward, { once: true })
  let timer: ReturnType<typeof setTimeout> | undefined
  const expired = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => {
      const reason = new DOMException('Das Zeitlimit des Aufrufs ist abgelaufen.', 'TimeoutError')
      controller.abort(reason)
      reject(reason)
    }, timeoutMs)
  })
  const running = task(controller.signal)
  running.catch(() => undefined)
  return Promise.race([running, expired]).finally(() => {
    clearTimeout(timer)
    signal?.removeEventListener('abort', forward)
  })
}

/**
 * Gelesener Rumpf als Merxo-Umschlag: `{ result }` bzw. `{ error: { code, message } }` – sonst null. Ein unbekannter
 * Code (neuerer Server) bleibt ein Umschlag und wird INTERNAL; JSON anderer Herkunft trägt diese Form nicht.
 */
function envelopeOf(text: string): Envelope | null {
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    return null
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null
  const envelope = parsed as Envelope
  if ('error' in envelope) {
    const error = envelope.error as { code?: unknown; message?: unknown } | null
    return error && typeof error === 'object' && typeof error.code === 'string' && typeof error.message === 'string'
      ? envelope
      : null
  }
  return 'result' in envelope ? envelope : null
}

/** Ist die Antwort nach einer Umleitung von einer fremden Adresse gekommen? */
function redirectedAway(response: Response): boolean {
  if (!response.redirected || !response.url) return false
  try {
    const here = typeof window !== 'undefined' ? window.location.origin : undefined
    return here !== undefined && new URL(response.url, here).origin !== here
  } catch {
    return true
  }
}

export function createApiClient(options: ApiClientOptions = {}): ApiClient {
  const baseUrl = (options.baseUrl ?? '/api').replace(/\/+$/, '')
  const doFetch: typeof fetch = (input, init) => (options.fetch ?? globalThis.fetch)(input, init)

  /** Fehler des Transports (keine Antwort von Merxo bzw. nur der Kante): meldet „nicht erreichbar“ */
  const unreachable = (reason: 'network' | 'timeout' | 'foreign' | 'edge', details: Record<string, unknown> = {}, cause?: unknown): AppError => {
    options.onReachability?.(false)
    const message = { network: NETWORK_MESSAGE, timeout: TIMEOUT_MESSAGE, foreign: FOREIGN_MESSAGE, edge: UNREACHABLE_MESSAGE }[reason]
    return new AppError('INTERNAL', message, { reason, ...details }, cause === undefined ? undefined : { cause })
  }

  /**
   * Sendet die Anfrage und liest den Rumpf ganz – beides innerhalb des Zeitlimits. Wirft AppError für `network`
   * (auch abgerissener Rumpf) und `timeout`; ein Abbruch durch den Aufrufer ohne Zeitgrund kommt unverändert zurück.
   */
  async function exchange(url: string, init: RequestInit, signal: AbortSignal | undefined, timeoutMs: number | undefined): Promise<{ response: Response; text: string }> {
    try {
      return await withDeadline(
        async (deadline) => {
          const response = await doFetch(url, { ...init, signal: deadline })
          return { response, text: await response.text() }
        },
        signal,
        timeoutMs,
      )
    } catch (cause) {
      if (isTimeoutReason(cause) || (signal?.aborted && isTimeoutReason(signal.reason))) throw unreachable('timeout', {}, cause)
      // Abbruch durch den Aufrufer (Seitenwechsel, neue Suche) ist kein Verbindungsproblem
      if (signal?.aborted) throw cause
      throw unreachable('network', {}, cause)
    }
  }

  async function request(
    method: 'GET' | 'POST',
    path: string,
    body: unknown,
    requestOptions: RequestOptions = {},
  ): Promise<Envelope> {
    const headers: Record<string, string> = { Accept: 'application/json' }
    const init: RequestInit = { method, credentials: 'same-origin', headers }
    if (method !== 'GET') {
      headers[CSRF_HEADER] = '1'
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        // Datei-Upload: Content-Type samt Grenze setzt der Browser
        init.body = body
      } else {
        headers['Content-Type'] = 'application/json'
        init.body = JSON.stringify(body ?? {})
      }
    }

    const { response, text } = await exchange(`${baseUrl}${path}`, init, requestOptions.signal, requestOptions.timeoutMs)
    const requestId = response.headers.get('x-request-id') ?? undefined
    // Umleitung auf eine fremde Adresse (Anmeldeseite eines WLANs): was dort steht, stammt nicht von Merxo
    const away = redirectedAway(response)
    const envelope = away ? null : envelopeOf(text)

    if (envelope?.error !== undefined) {
      options.onReachability?.(true)
      const error = fromErrorPayload(envelope.error)
      const withId = requestId ? new AppError(error.code, error.message, { ...error.details, requestId }) : error
      // Merxo antwortet; 5xx, LOCK_TIMEOUT und 429 sind eine Störung des Servers, alles andere ein fachlicher Fehler
      const disturbed = response.status >= 500 || response.status === 429 || withId.code === 'LOCK_TIMEOUT'
      reachOfError.set(withId, disturbed ? 'server' : 'ok')
      if (withId.code === 'UNAUTHENTICATED' && !requestOptions.silentUnauthenticated)
        options.onUnauthenticated?.(withId)
      throw withId
    }
    if (response.ok && envelope && 'result' in envelope) {
      options.onReachability?.(true)
      return envelope
    }

    // Antwort ohne Umschlag. Mit Kennzeichen der Kante: 5xx = Anwendung nicht erreichbar (Neustart, gestört); andere
    // Antworten der Kante (413 bei zu großem Rumpf) sind ihre Antwort auf die Anfrage. Ohne Kennzeichen stammt die
    // Antwort nicht von Merxo – jeder Status, auch 200/3xx/4xx mit HTML.
    if (!away && response.headers.has(EDGE_HEADER)) {
      if (response.status >= 500) throw unreachable('edge', { status: response.status, requestId })
      options.onReachability?.(true)
      const error = new AppError(STATUS_CODES[response.status] ?? 'INTERNAL', undefined, { status: response.status, requestId })
      reachOfError.set(error, 'ok')
      if (error.code === 'UNAUTHENTICATED' && !requestOptions.silentUnauthenticated) options.onUnauthenticated?.(error)
      throw error
    }
    throw unreachable('foreign', { status: response.status, requestId })
  }

  async function health(healthOptions: HealthOptions = {}): Promise<HealthCheck> {
    const started = Date.now()
    const init: RequestInit = { method: 'GET', cache: 'no-store', credentials: 'same-origin', headers: { Accept: 'application/json' } }
    let exchanged: { response: Response; text: string }
    try {
      exchanged = await exchange(`${baseUrl}/health`, init, healthOptions.signal, healthOptions.timeoutMs ?? HEALTH_TIMEOUT_MS)
    } catch (cause) {
      const reason = reachReasonOf(cause)
      if (reason !== 'network' && reason !== 'timeout') throw cause
      return { reason, status: null, serverTime: null, elapsedMs: Date.now() - started }
    }
    const { response, text } = exchanged
    const elapsedMs = Date.now() - started
    const away = redirectedAway(response)
    interface HealthBody {
      product?: unknown
      time?: unknown
    }
    let body: HealthBody | null = null
    if (!away) {
      try {
        const parsed: unknown = JSON.parse(text)
        if (parsed && typeof parsed === 'object') body = parsed as HealthBody
      } catch {
        body = null
      }
    }
    const merxo = body?.product === brand.name
    const time = merxo && typeof body?.time === 'string' ? new Date(body.time) : null
    const serverTime = time && Number.isFinite(time.getTime()) ? time : null
    if (merxo && response.status === 200 && serverTime) {
      options.onReachability?.(true)
      return { reason: 'ok', status: 200, serverTime, elapsedMs }
    }
    options.onReachability?.(false)
    // Merxo antwortet, ist aber nicht bereit (503: Datenbank, Migrationen) bzw. nur die Kante antwortet
    const edge = merxo || (!away && response.headers.has(EDGE_HEADER))
    return { reason: edge ? 'edge' : 'foreign', status: response.status, serverTime, elapsedMs }
  }

  const isMeta = (value: unknown): value is ActionMeta =>
    typeof value === 'object' && value !== null && Array.isArray((value as ActionMeta).refs)

  // Zeitpunkte: JSON kennt nur Text – das Ausgabeschema des Vertrags sagt, wo ein Date hingehört (web/api/dates.ts).
  // Wer nur den Namen kennt (Rückgängig-Angebot), bekommt den Vertrag aus der Registry.
  const known = (name: string): AnyActionContract | undefined => (actionContracts as Record<string, AnyActionContract | undefined>)[name]

  async function call(name: string, input: unknown, runOptions: RunOptions = {}, contract: AnyActionContract | undefined = known(name)): Promise<ActionOutcome<unknown>> {
    const body: Record<string, unknown> = { input: input ?? {} }
    if (runOptions.idempotencyKey) body.idempotencyKey = runOptions.idempotencyKey
    if (runOptions.expectedEffectHash) body.expectedEffectHash = runOptions.expectedEffectHash
    if (runOptions.approval) body.approval = runOptions.approval
    const envelope = await request('POST', `/actions/${encodeURIComponent(name)}`, body, {
      signal: runOptions.signal,
      timeoutMs: runOptions.timeoutMs,
    })
    return { output: reviveDates(contract?.output, envelope.result), meta: isMeta(envelope.meta) ? envelope.meta : null }
  }
  const runByName = (name: string, input: unknown, runOptions?: RunOptions): Promise<ActionOutcome<unknown>> => call(name, input, runOptions)

  const sessionCall = async (
    method: 'GET' | 'POST',
    path: string,
    body?: unknown,
    requestOptions?: RequestOptions,
  ): Promise<SessionPayload> => (await request(method, path, body, requestOptions)).result as SessionPayload

  async function authCall<T>(name: AuthRouteName, body?: unknown, requestOptions: RequestOptions = {}, query = ''): Promise<T> {
    const route = AUTH_ROUTES[name]
    const silentUnauthenticated = !route.session || CREDENTIAL_ROUTES.has(name)
    const envelope = await request(route.method, `${route.path}${query}`, route.method === 'GET' ? undefined : (body ?? {}), {
      ...requestOptions,
      silentUnauthenticated,
    })
    return envelope.result as T
  }
  const publicMail = async <T>(path: string, body: unknown): Promise<T> =>
    (await request('POST', path, body, { silentUnauthenticated: true })).result as T

  return {
    async run(contract, input, runOptions) {
      return (await call(contract.name, input, runOptions, contract)).output as never
    },
    async runDetailed(contract, input, runOptions) {
      return (await call(contract.name, input, runOptions, contract)) as never
    },
    runByName,
    async plan(contract, input, planOptions) {
      const envelope = await request(
        'POST',
        `/actions/${encodeURIComponent(contract.name)}/plan`,
        { input: input ?? {} },
        { signal: planOptions?.signal, timeoutMs: planOptions?.timeoutMs },
      )
      return envelope.result as never
    },
    async authConfig(requestOptions) {
      return (await request('GET', '/auth/config', undefined, { ...requestOptions, silentUnauthenticated: true }))
        .result as AuthConfig
    },
    login: (input) => sessionCall('POST', '/auth/login', input, { silentUnauthenticated: true }),
    async logout(input) {
      await request('POST', '/auth/logout', input ?? {}, { silentUnauthenticated: true })
    },
    session: (requestOptions) => sessionCall('GET', '/session', undefined, requestOptions),
    health,
    selectTenant: (tenantId) => sessionCall('POST', '/session/tenant', { tenantId }),
    setLocation: (locationId) => sessionCall('POST', '/session/location', { locationId }),
    async upload(file, uploadOptions = {}) {
      const body = new FormData()
      const name = uploadOptions.name ?? (file instanceof File ? file.name : undefined)
      // als File mit Namen senden – der Server führt den Namen neben dem Inhalt
      const part = name && !(file instanceof File && file.name === name) ? new File([file], name, { type: file.type }) : file
      body.append('file', part)
      const query = uploadOptions.kind ? `?kind=${encodeURIComponent(uploadOptions.kind)}` : ''
      return (await request('POST', `/files${query}`, body, { signal: uploadOptions.signal })).result as UploadedFile
    },

    loginMfa: (input) => authCall('loginMfa', input),
    mfaSetup: (input) => authCall('mfaSetup', input ?? {}),
    mfaConfirm: (input) => authCall('mfaConfirm', input),
    mfaDisable: (input) => authCall('mfaDisable', input),
    recoveryCodes: (input) => authCall('recoveryCodes', input),
    reauth: (input) => authCall('reauth', input),
    changePassword: (input) => authCall('changePassword', input),
    passwordCheck: (input, requestOptions) => authCall('passwordCheck', input, { signal: requestOptions?.signal }),
    forgotPassword: (input) => authCall('forgotPassword', input),
    resetCheck: (input) => authCall('resetCheck', input),
    resetPassword: (input) => authCall('resetPassword', input),
    sessions: () => authCall('sessions'),
    revokeSession: (input) => authCall('revokeSession', input),
    forgetDevice: (input) => authCall('forgetDevice', input),
    securityEvents: (input) =>
      authCall('securityEvents', undefined, {}, input?.limit ? `?limit=${encodeURIComponent(String(input.limit))}` : ''),
    invitationInfo: (input) => authCall('invitationInfo', input),
    acceptInvitation: (input) => authCall('acceptInvitation', input),
    unsubscribeInfo: (input) => publicMail(MAIL_PUBLIC_ROUTES.unsubscribeInfo, input),
    unsubscribe: (input) => publicMail(MAIL_PUBLIC_ROUTES.unsubscribe, input),
  }
}

// ---- der eine Client der Anwendung ------------------------------------------------------------------------
// Die Hülle hängt hier Sitzung und Verbindungsanzeige ein (configureApiClient). Tests und der Schaukasten reichen
// stattdessen einen eigenen Client über <ApiProvider client={…}> herein.

const hooks: Required<Pick<ApiClientOptions, 'onUnauthenticated' | 'onReachability'>> = {
  onUnauthenticated: () => undefined,
  onReachability: () => undefined,
}

let current: ApiClient = createApiClient({
  onUnauthenticated: (error) => hooks.onUnauthenticated(error),
  onReachability: (reachable) => hooks.onReachability(reachable),
})

export const getApiClient = (): ApiClient => current

/** Ersetzt den Client der Anwendung (Tests). Liefert eine Funktion, die den vorherigen wieder einsetzt. */
export function setApiClient(client: ApiClient): () => void {
  const previous = current
  current = client
  return () => {
    current = previous
  }
}

export function configureApiClient(next: Pick<ApiClientOptions, 'onUnauthenticated' | 'onReachability'>): void {
  if (next.onUnauthenticated) hooks.onUnauthenticated = next.onUnauthenticated
  if (next.onReachability) hooks.onReachability = next.onReachability
}

/** Aktion außerhalb von React ausführen (Ereignisbehandlung, Hilfsfunktionen). In Komponenten: die Hooks. */
export const runAction = <C extends AnyActionContract>(
  contract: C,
  input: InputArg<C>,
  options?: RunOptions,
): Promise<OutputOf<C>> => current.run(contract, input, options)

/** Vorschau einer ändernden Aktion außerhalb von React. In Komponenten: useConfirmedAction. */
export const planAction = <C extends AnyActionContract>(
  contract: C,
  input: InputArg<C>,
  options?: Pick<RunOptions, 'signal' | 'timeoutMs'>,
): Promise<ActionPlan<InputOf<C>>> => current.plan(contract, input, options)

/**
 * Gesundheitsprüfung über den Client der Anwendung (GET /api/health, §10.1 der Spezifikation „Kasse ohne Verbindung“):
 * wirft nie (außer bei Abbruch durch den Aufrufer), sondern liefert die Einordnung.
 *
 *   const check = await checkHealth()            // { reason: 'ok' | 'edge' | 'foreign' | 'network' | 'timeout', … }
 */
export const checkHealth = (options?: HealthOptions): Promise<HealthCheck> => current.health(options)

/**
 * Datei über POST /api/files hochladen (docs/API.md §7) – außerhalb von React; in Komponenten geht auch
 * `useApiClient().upload(file)`. `'image'` bzw. `{ kind: 'image' }` lässt nur Bilder zu.
 *
 *   const stored = await uploadFile(file, 'image')
 *   await save({ id, images: [{ fileId: stored.id }] })      // anhängen ist Sache der Aktion des Moduls
 */
export const uploadFile = (file: Blob, options?: UploadOptions | 'image'): Promise<UploadedFile> =>
  current.upload(file, typeof options === 'string' ? { kind: options } : options)
